import { Fragment, useEffect, useState } from 'react'
import type { EventBus } from '../core/EventBus'
import type { PositionSizeReadoutPayload } from '../utils/positionSizeIndicator'

/**
 * The POSITION_SIZE readout pill — the React face of the sizing indicator.
 *
 * Subscribes to `positionSize:readout` (relayed by ChartManager from the
 * indicator calc's `extendData.onReadout`) and renders top-right of the candle
 * pane. Deliberately MINIMAL at rest: the share count and which level is
 * sizing it ("1,219 sh @ R1"). Everything else — direction, risk, reference
 * price, distance, realized dollars — appears on hover. A trader glances at
 * this dozens of times a session; the full row every time is noise.
 *
 * Hover expands the pill DOWNWARD into a label/value list: direction, stop,
 * distance, shares, position, risk, and the cap when it bound. Two columns
 * (labels left, values right-aligned and tabular) so six numbers stay
 * scannable. Both dollar figures are labelled because capital deployed and
 * risk-if-stopped are wildly different numbers and an unlabelled one invites
 * the wrong read.
 *
 * The left accent edge carries direction (green long / red short), muted when
 * the readout is dashed.
 */

const ACCENT_LONG = '#089981'
const ACCENT_SHORT = '#f23645'
const ACCENT_MUTED = '#787b86'

function fmtPrice(v: number, precision: number): string {
  return v.toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  })
}

function fmtMoney(v: number): string {
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Whole-dollar risk shows as $500, fractional as $500.50. */
function fmtRisk(v: number): string {
  return Number.isInteger(v) ? `$${v.toLocaleString('en-US')}` : `$${fmtMoney(v)}`
}

/** The at-rest text: just the answer. Dashes stay short but keep their cause. */
export function compactSizingText(payload: PositionSizeReadoutPayload): string {
  const { readout, source } = payload
  switch (readout.state) {
    case 'ok':
      return (
        `${readout.shares.toLocaleString('en-US')} sh @ ${readout.label}` +
        // A clamped size looks like a mis-sized trade unless the reason rides
        // along, so the cap marker survives even in the minimal at-rest view.
        (readout.capped ? ' · cap' : '')
      )
    // 'no-data' is overloaded upstream: it means "the source produced no level",
    // which for a MANUAL source is a thing the trader fixes in one gesture, and
    // for a computed source is just "not resolved yet" (MA warm-up, first
    // session in view). "no data" reads as broken in the first case - the
    // indicator is working perfectly, it is waiting for a line.
    case 'no-data':
      return source === 'manual' ? '- add a line' : '- no data'
    // Trader language, not engine language: the level being on the non-stop
    // side means price has already cleared it in your favor.
    case 'wrong-side':
      return '- in the money'
    case 'too-close':
      return '- too close'
  }
}

/** One label/value pair per line of the expanded panel. */
export interface SizingDetailRow {
  label: string
  value: string
}

/**
 * The hover detail, as discrete rows rather than one joined string. Two cells
 * per row lets the panel align labels left and values right, which is what
 * makes a six-item readout scannable; a single run-on line is not.
 */
export function detailSizingRows(payload: PositionSizeReadoutPayload): SizingDetailRow[] {
  const { readout, direction, maxPositionDollars, precision, source } = payload
  const dir = direction.toUpperCase()
  const head: SizingDetailRow[] = [{ label: 'Direction', value: dir }]
  switch (readout.state) {
    case 'ok': {
      const rows: SizingDetailRow[] = [
        ...head,
        { label: 'Stop', value: `${readout.label} ${fmtPrice(readout.refPrice, precision)}` },
        { label: 'Distance', value: fmtPrice(readout.distance, precision) },
        { label: 'Shares', value: readout.shares.toLocaleString('en-US') },
        { label: 'Position', value: `$${fmtMoney(readout.positionValue)}` },
        { label: 'Risk', value: `$${fmtMoney(readout.realized)}` },
      ]
      // Only shown when it actually bound, so its presence IS the signal that
      // the ceiling, not the risk budget, chose this size.
      if (readout.capped) rows.push({ label: 'Cap', value: fmtRisk(maxPositionDollars) })
      return rows
    }
    // Dash states keep the same two-column rhythm: direction on its own row,
    // the cause on the next. Folding both into one cell reads as a run-on.
    // Same split as the pill: a manual source with nothing drawn gets the
    // instruction, a computed source gets the honest "not yet".
    case 'no-data':
      return [
        ...head,
        {
          label: 'Status',
          value:
            source === 'manual'
              ? 'Draw a horizontal line to size against'
              : 'No relevant level yet',
        },
      ]
    case 'wrong-side':
      return [...head, { label: 'Status', value: 'In the money' }]
    case 'too-close':
      return [...head, { label: 'Status', value: 'Level too close to size against' }]
  }
}

export function accentColor(payload: PositionSizeReadoutPayload): string {
  if (payload.readout.state !== 'ok') return ACCENT_MUTED
  return payload.direction === 'long' ? ACCENT_LONG : ACCENT_SHORT
}

/**
 * Lucide's `calculator` glyph, inlined. charts-core deliberately carries no
 * icon-set dependency (see ChartBodyContextMenu for the same pattern), so the
 * node is transcribed rather than imported. Same geometry as lucide-react
 * v0.576.0, so it sits consistently beside the app's other icons.
 */
function CalculatorIcon() {
  return (
    <svg
      width={11}
      height={11}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0, opacity: 0.75 }}
    >
      <rect width="16" height="20" x="4" y="2" rx="2" />
      <line x1="8" x2="16" y1="6" y2="6" />
      <line x1="16" x2="16" y1="14" y2="18" />
      <path d="M16 10h.01" />
      <path d="M12 10h.01" />
      <path d="M8 10h.01" />
      <path d="M12 14h.01" />
      <path d="M8 14h.01" />
      <path d="M12 18h.01" />
      <path d="M8 18h.01" />
    </svg>
  )
}

export interface PositionSizeReadoutProps {
  eventBus: EventBus
  /** Whether a POSITION_SIZE indicator is mounted — gates rendering so a
   *  removed indicator's last payload doesn't linger. */
  active: boolean
  top: number
  right: number
}

export function PositionSizeReadout({ eventBus, active, top, right }: PositionSizeReadoutProps) {
  const [payload, setPayload] = useState<PositionSizeReadoutPayload | null>(null)
  const [hovered, setHovered] = useState(false)

  useEffect(() => {
    const handler = (next: PositionSizeReadoutPayload) => {
      // Calc re-runs on every data tick; only re-render on a material change.
      setPayload((prev) => (prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next))
    }
    eventBus.on('positionSize:readout', handler)
    return () => {
      eventBus.off('positionSize:readout', handler)
    }
  }, [eventBus])

  // Drop the stale payload when the indicator is removed, so re-adding it
  // starts from the fresh calc instead of flashing the old numbers.
  useEffect(() => {
    if (!active) {
      setPayload(null)
      setHovered(false)
    }
  }, [active])

  if (!active || !payload) return null

  const dashed = payload.readout.state !== 'ok'
  return (
    <div
      style={{
        position: 'absolute',
        top,
        right,
        zIndex: 10,
        padding: hovered ? '4px 8px 6px' : '3px 8px',
        borderRadius: 10,
        background: 'rgba(19,23,34,0.85)',
        border: '1px solid #2a2e39',
        borderLeft: `3px solid ${accentColor(payload)}`,
        color: dashed ? '#787b86' : '#d1d4dc',
        fontSize: 11,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        cursor: 'default',
        userSelect: 'none',
        // Column, so the detail list grows DOWNWARD out of the summary row.
        // The pill is anchored top-right, so downward growth costs no chart
        // space until the trader actually hovers.
        display: 'flex',
        flexDirection: 'column',
        gap: hovered ? 4 : 0,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="status"
      aria-live="polite"
      data-testid="position-size-readout"
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <CalculatorIcon />
        <span>{compactSizingText(payload)}</span>
      </span>

      {hovered && (
        <dl
          data-testid="position-size-detail"
          style={{
            display: 'grid',
            // Labels size to content, values take the rest and right-align, so
            // the numbers form a column the eye can run down.
            gridTemplateColumns: 'auto 1fr',
            columnGap: 14,
            rowGap: 2,
            margin: 0,
            paddingTop: 4,
            borderTop: '1px solid #2a2e39',
          }}
        >
          {detailSizingRows(payload).map((row) => (
            <Fragment key={row.label}>
              <dt style={{ color: '#787b86' }}>{row.label}</dt>
              <dd style={{ margin: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {row.value}
              </dd>
            </Fragment>
          ))}
        </dl>
      )}
    </div>
  )
}
